import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { BehaviorSubject, catchError, Observable, of } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import {
  PaginatedUsersResponse,
  RoleOption,
  UserDTO,
  UserGetDTO,
  UserStatus,
  UserUpdateDTO,
} from '../interfaces/users-interface';

export interface ApiConfig {
  apiUrl?: string | null;
}

@Injectable({
  providedIn: 'root',
})
export class UsersService {
  private http = inject(HttpClient);
  private usersSubject = new BehaviorSubject<UserGetDTO[]>([]);
  public users$ = this.usersSubject.asObservable();
  private totalUsersSubject = new BehaviorSubject<number>(0);
  public totalUsers$ = this.totalUsersSubject.asObservable();
  private loadingSubject = new BehaviorSubject<boolean>(false);
  public loading$ = this.loadingSubject.asObservable();
  private config: ApiConfig | null = null;

  configure(config: ApiConfig): void {
    this.config = config;
  }

  getUsers(
    page = 1,
    limit = 10,
    search?: string,
    status?: UserStatus
  ): Observable<PaginatedUsersResponse> {
    const apiUrl = this.getApiUrl();
    const url = `${apiUrl}/api/users/`;
    this.loadingSubject.next(true);

    let params = new HttpParams().set('page', page.toString()).set('limit', limit.toString());

    if (search && search.trim()) {
      params = params.set('q', search.trim());
    }

    if (status && status !== 'all') {
      params = params.set('status', status);
    }

    return this.http.get<PaginatedUsersResponse>(url, { params }).pipe(
      tap((response) => {
        this.usersSubject.next(response.data);
        this.totalUsersSubject.next(response.total);
        this.loadingSubject.next(false);
      }),
      map((response) => response),
      catchError((error) => {
        this.loadingSubject.next(false);
        throw error;
      })
    );
  }

  getRoles(): Observable<RoleOption[]> {
    const apiUrl = this.getApiUrl();
    const url = `${apiUrl}/api/roles/?only_names=true`;

    return this.http.get<string[]>(url).pipe(
      map((roles: string[]) =>
        roles.map((role) => ({
          label: role,
          value: role,
        }))
      ),
      catchError((error) => {
        console.error('Error al cargar roles:', error);
        return of([
          { label: 'admin', value: 'admin' },
          { label: 'user', value: 'user' },
          { label: 'editor', value: 'editor' },
        ]);
      })
    );
  }

  getUserById(id: number): Observable<UserGetDTO> {
    const apiUrl = this.getApiUrl();
    const url = `${apiUrl}/api/users/${id}/`;
    return this.http.get<UserGetDTO>(url);
  }

  createUser(user: UserDTO): Observable<UserDTO> {
    const apiUrl = this.getApiUrl();
    const url = `${apiUrl}/api/users/`;
    return this.http.post<UserDTO>(url, user);
  }

  updateUser(id: number, user: Partial<UserUpdateDTO>): Observable<UserGetDTO> {
    const apiUrl = this.getApiUrl();
    const url = `${apiUrl}/api/users/${id}/`;
    return this.http.put<UserGetDTO>(url, user);
  }

  deleteUser(id: number): Observable<void> {
    const apiUrl = this.getApiUrl();
    const url = `${apiUrl}/api/users/${id}/`;
    return this.http.delete<void>(url);
  }

  toggleUserStatus(id: number, isActive: boolean): Observable<UserGetDTO> {
    const apiUrl = this.getApiUrl();
    const url = `${apiUrl}/api/users/${id}/`;

    const payload = { is_active: isActive };

    return this.http.put<UserGetDTO>(url, payload);
  }

  blockUser(id: number, isBlocked: boolean): Observable<UserGetDTO> {
    const apiUrl = this.getApiUrl();
    const url = `${apiUrl}/api/users/${id}/`;

    console.log('Block user:', { id, isBlocked, url });
    const payload = { is_blocked: isBlocked };

    return this.http.put<UserGetDTO>(url, payload).pipe(
      tap((response) => {
        console.log('Block response:', response);
        this.getUsers().subscribe();
      }),
      catchError((error) => {
        console.error('Error in blockUser:', error);
        throw error;
      })
    );
  }

  private getApiUrl(): string {
    if (!this.config) {
      throw new Error('UsersService no está configurado. Llama a configure() primero.');
    }
    return <string>this.config.apiUrl;
  }
}
